/**
 * Cmd-K command palette for wayfinding across the 20+ surfaces (CD review M1).
 *
 * Search index: every SurfaceDef's label + group + section + keywords (lowercased,
 * substring match). Ranking: exact label > label prefix > label substring > other
 * fields. Keyboard: ↑/↓ (and j/k) navigate, Enter jumps to the surface (hash
 * router), Esc closes. Opened by ⌘K / Ctrl-K (mounted in App).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { SURFACES, type SurfaceDef } from './surfaces/registry';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Current surface id, highlighted in the list. */
  activeId?: string;
}

interface Hit { surface: SurfaceDef; score: number; matchedOn: string }

export function score(surface: SurfaceDef, q: string): Hit | null {
  if (!q) return { surface, score: 0, matchedOn: '' };
  const ql = q.toLowerCase();
  const label = surface.label.toLowerCase();
  if (label === ql) return { surface, score: 100, matchedOn: 'label' };
  if (label.startsWith(ql)) return { surface, score: 80, matchedOn: 'label' };
  if (label.includes(ql)) return { surface, score: 60, matchedOn: 'label' };
  if ((surface.keywords ?? []).some(k => k.toLowerCase().includes(ql))) return { surface, score: 50, matchedOn: 'keyword' };
  if (surface.group.toLowerCase().includes(ql)) return { surface, score: 30, matchedOn: 'group' };
  if (surface.section.toLowerCase().includes(ql)) return { surface, score: 20, matchedOn: 'section' };
  return null;
}

/**
 * Rank all surfaces for a query, highest-score first. Exported so the
 * ⌘K keyword-coverage CI gate (CD R11 #8) can assert that operator
 * vocabulary ("ppe" → Supply Chain, "icu nurses" → Workforce, etc.)
 * still resolves — catching keyword rot the way registry-invariants
 * catches cadence-map drift. Mirrors the in-component `hits` memo.
 */
export function searchSurfaces(q: string): Hit[] {
  const query = q.trim();
  if (!query) return SURFACES.map(s => ({ surface: s, score: 0, matchedOn: '' }));
  return SURFACES.map(s => score(s, query))
    .filter((h): h is Hit => h !== null)
    .sort((a, b) => b.score - a.score);
}

export function CommandPalette({ open, onClose, activeId }: CommandPaletteProps) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state on open + focus the input.
  useEffect(() => {
    if (open) { setQ(''); setCursor(0); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  const hits = useMemo<Hit[]>(() => {
    if (!open) return [];
    return searchSurfaces(q);
  }, [open, q]);

  const max = Math.max(0, hits.length - 1);
  const safeCursor = Math.min(cursor, max);

  // Keyboard navigation while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown' || (e.key === 'j' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault(); setCursor(c => Math.min(c + 1, max)); return;
      }
      if (e.key === 'ArrowUp' || (e.key === 'k' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const pick = hits[safeCursor]?.surface;
        if (pick) { window.location.hash = `#/${pick.id}`; onClose(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, max, hits, safeCursor, onClose]);

  // Scroll the active row into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cursor="${safeCursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [safeCursor, open]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh',
      }}
    >
      <div
        role="dialog"
        aria-label="Command palette"
        onClick={e => e.stopPropagation()}
        /* Brand glass-dark CHROME — palette frame. The class owns bg / blur /
           border / radius / shadow + the a11y fallbacks; inline keeps layout
           only. The list rows inside stay solid (selected row uses the solid
           accent-subtle fill), per "glass SHELL, solid CORE". */
        className="bm-glass-dark-strong"
        style={{
          width: 'min(640px, 92vw)', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setCursor(0); }}
          placeholder="Jump to surface — type a name, group or keyword…"
          aria-label="Search surfaces"
          style={{
            padding: '14px 18px', font: '15px var(--bm-font-sans)',
            background: 'transparent', color: 'var(--bm-text)',
            border: 'none', borderBottom: '1px solid var(--bm-border)', outline: 'none',
          }}
        />
        <div ref={listRef} style={{ overflow: 'auto', padding: '4px 0' }}>
          {hits.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--bm-text-muted)' }}>No surface matches "{q}".</div>
          ) : (
            hits.map((h, i) => {
              const sel = i === safeCursor;
              const isActive = h.surface.id === activeId;
              return (
                <div
                  key={h.surface.id}
                  data-cursor={i}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => { window.location.hash = `#/${h.surface.id}`; onClose(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer',
                    background: sel ? 'var(--bm-accent-subtle)' : 'transparent',
                    borderLeft: sel ? '2px solid var(--bm-accent)' : '2px solid transparent',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--bm-text)' }}>
                      {h.surface.label}
                      {isActive && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--bm-prov-confirmed)', fontFamily: 'var(--bm-font-mono)' }}>● current</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--bm-text-subtle)', fontFamily: 'var(--bm-font-mono)' }}>
                      {h.surface.group} · {h.surface.section}{h.matchedOn ? ` · matched ${h.matchedOn}` : ''}
                    </div>
                  </span>
                </div>
              );
            })
          )}
        </div>
        <div style={{ padding: '6px 14px', font: '11px var(--bm-font-mono, ui-monospace, monospace)', color: 'var(--bm-text-subtle)', borderTop: '1px solid var(--bm-border)', display: 'flex', gap: 14 }}>
          <span>↑↓ navigate</span><span>↵ jump</span><span>esc close</span><span style={{ marginLeft: 'auto' }}>{hits.length} of {SURFACES.length}</span>
        </div>
      </div>
    </div>
  );
}
